import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@siliconx/shared/ui/Button';
import { Select } from '@siliconx/shared/ui/Select';
import { Field, TextInput } from '@siliconx/shared/ui/Field';
import { conditionLabel } from '@siliconx/shared/ui/ConditionBadge';
import { formatUsd, formatRangeText, stripMake } from '@siliconx/shared/fmv/browse';
import type { ConditionBand } from '@siliconx/shared';
import { MakeModelPicker, type ModelSuggestItem } from '../shared/MakeModelPicker';
import { api, throwIfNotOk } from '../../lib/api';

/**
 * SIL-1103 — "Add SKU line" inline panel for the purchase-analysis builder
 * (design spec, purchase-decision section). Replaces the v1 `window.prompt`
 * raw-skuId entry with the SAME catalog navigation the Add Asset screen uses
 * (`apps/customer/src/components/shared/MakeModelPicker.tsx`, unchanged) —
 * see `apps/customer/src/routes/assets/submit.tsx` for the prior art this
 * mirrors (make/model state, onPick/onModelChange/onClear wiring).
 *
 * Deliberately an inline panel, not a modal — the lines table and the result
 * header stay visible/live while picking, matching AddClassLineButton's
 * always-visible control rather than AddFromListingButton's modal (that one
 * browses a much longer, unrelated list).
 *
 * A catalog pick is required: `onPick` is the only thing that can enable
 * Add. Free-text typing in the Model field alone never does (MakeModelPicker
 * allows off-catalog text so the Add Asset form can submit it, but this
 * screen's line always resolves to a comp-priced skuId — see design doc
 * "Rows are added from a SKU picker"). A master ("Unknown — price as family
 * range") is a valid pick too; its `skuId` is posted as-is, no special
 * handling — pricing for a family-range SKU flows through the same compute
 * path as any other.
 */
export function SkuLinePicker({
  analysisId,
  onAdded,
  onCancel,
}: {
  analysisId: string;
  onAdded: () => void;
  onCancel: () => void;
}) {
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [pick, setPick] = useState<ModelSuggestItem | null>(null);
  const [quantity, setQuantity] = useState('1');
  const [conditionBand, setConditionBand] = useState<ConditionBand>('normal_enterprise_wear');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const qty = Number(quantity);
  const canAdd = !!pick && Number.isInteger(qty) && qty > 0;

  function reset() {
    setMake('');
    setModel('');
    setPick(null);
    setQuantity('1');
    setConditionBand('normal_enterprise_wear');
    setError(null);
  }

  function handleCancel() {
    reset();
    onCancel();
  }

  async function handleAdd() {
    if (!pick || !canAdd) return;
    setAdding(true);
    setError(null);
    try {
      const res = await api.api.client['purchase-analyses'][':id'].lines.$post({
        param: { id: analysisId },
        json: {
          lineType: 'sku',
          skuId: pick.skuId,
          quantity: qty,
          conditionBand,
          unitAcquisitionCostUsd: 0,
        },
      });
      await throwIfNotOk(res);
      reset();
      onAdded();
    } catch (ex) {
      setError((ex as Error).message ?? 'Could not add this line.');
    } finally {
      setAdding(false);
    }
  }

  return (
    <div className="space-y-3 rounded-lg border border-border bg-muted/20 p-4">
      <MakeModelPicker
        make={make}
        model={model}
        onMakeChange={(v) => {
          setMake(v);
          setPick(null);
        }}
        onModelChange={(v) => {
          // Free-text edit — a stale pick must not survive it (same rule as
          // submit.tsx: onPick is the only thing that can set a pick).
          setModel(v);
          setPick(null);
        }}
        onPick={(m) => {
          setModel(stripMake(m.modelName, make));
          setPick(m);
        }}
        onClear={() => {
          setModel('');
          setPick(null);
        }}
      />

      <div className="rounded-md border border-border bg-card px-3 py-2 text-sm">
        {pick ? (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <span className="font-medium text-foreground">{stripMake(pick.modelName, make)}</span>
              {pick.assetClassName && (
                <span className="ml-2 text-xs text-muted-foreground">{pick.assetClassName}</span>
              )}
            </div>
            <span className="text-xs text-muted-foreground">{priceLabel(pick)}</span>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">Pick a model from the catalog above to add a line.</p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Quantity">
          <TextInput
            type="number"
            inputMode="numeric"
            min={1}
            step={1}
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
          />
        </Field>
        <Field label="Condition">
          <Select value={conditionBand} onChange={(e) => setConditionBand(e.target.value as ConditionBand)}>
            {CONDITION_BANDS.map((b) => (
              <option key={b} value={b}>
                {conditionLabel(b)}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={handleCancel}
          className="rounded-md px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted"
        >
          Cancel
        </button>
        <Button type="button" onClick={() => void handleAdd()} disabled={!canAdd || adding}>
          {adding && <Loader2 className="mr-1 size-4 animate-spin" />}
          {adding ? 'Adding…' : 'Add'}
        </Button>
      </div>
    </div>
  );
}

const CONDITION_BANDS: ConditionBand[] = ['new', 'like_new', 'normal_enterprise_wear', 'high_wear', 'unknown'];

/** Mirrors MakeModelPicker's own `rowPriceLabel` — a master's
 * `baselinePriceUsd` is NULL by construction (migration 789: masters price
 * via family range, never a point price), so a master must read its range
 * fields, never `baselinePriceUsd`, or a priced family range wrongly prints
 * "no comps" (see MakeModelPicker's header comment on the prod defect this
 * guards against). */
function priceLabel(pick: ModelSuggestItem): string {
  if (pick.grain === 'master') {
    const range = formatRangeText(pick.rangeLowUsd, pick.rangeHighUsd);
    return range !== '—' ? range : 'no comps';
  }
  return formatUsd(pick.baselinePriceUsd) ?? 'no comps';
}
